import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Schedule as PendingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

function TenantApprovalPage() {
  const theme = useTheme();
  const [pendingTenants, setPendingTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState({
    open: false,
    tenant: null,
    action: null,
  });
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPendingTenants();
  }, []);

  const loadPendingTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/onboarding/tenants/pending', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setPendingTenants(data.tenants);
      } else {
        toast.error(data.message || 'Failed to load pending tenants');
      }
    } catch (error) {
      console.error('Error loading pending tenants:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = (tenant, action) => {
    setApprovalDialog({
      open: true,
      tenant,
      action,
    });
    setRejectionReason('');
  };

  const handleConfirmApproval = async () => {
    const { tenant, action } = approvalDialog;

    try {
      setProcessing(true);
      const response = await fetch(`/api/v1/onboarding/tenant/${tenant._id}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
        },
        body: JSON.stringify({
          approved: action === 'approve',
          rejectionReason: action === 'reject' ? rejectionReason : undefined,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`Tenant ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
        setApprovalDialog({ open: false, tenant: null, action: null });
        loadPendingTenants(); // Refresh the list
      } else {
        toast.error(data.message || 'Failed to process approval');
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseDialog = () => {
    setApprovalDialog({ open: false, tenant: null, action: null });
    setRejectionReason('');
  };

  const getIndustryColor = (industry) => {
    const colors = {
      technology: 'primary',
      healthcare: 'success',
      finance: 'warning',
      education: 'info',
      retail: 'secondary',
      manufacturing: 'error',
      'real-estate': 'default',
      consulting: 'primary',
      other: 'default',
    };
    return colors[industry] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh',
      }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Tenant Approval Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and approve pending tenant applications
        </Typography>

        <Button
          startIcon={<RefreshIcon />}
          onClick={loadPendingTenants}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight={700}>
                {pendingTenants.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Applications
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Tenants */}
      {pendingTenants.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PendingIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No pending applications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All tenant applications have been processed
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {pendingTenants.map((tenant) => (
            <Grid item xs={12} md={6} key={tenant._id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={2}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {tenant.name}
                        </Typography>
                        <Chip
                          label={tenant.status}
                          color="warning"
                          size="small"
                          icon={<PendingIcon />}
                        />
                      </Box>
                      <Chip
                        label={tenant.companyInfo?.industry || 'Other'}
                        color={getIndustryColor(tenant.companyInfo?.industry)}
                        size="small"
                      />
                    </Box>

                    <Divider />

                    {/* Contact Information */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Contact Person
                      </Typography>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {tenant.contactPerson?.firstName}
                            {' '}
                            {tenant.contactPerson?.lastName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {tenant.contactPerson?.email}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {tenant.contactPerson?.phone}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Company Information */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Company Details
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <strong>Size:</strong>
                          {' '}
                          {tenant.companyInfo?.size || 'Not specified'}
                        </Typography>
                        {tenant.website && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WebsiteIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {tenant.website}
                            </Typography>
                          </Box>
                        )}
                        {tenant.companyInfo?.description && (
                          <Typography variant="body2">
                            <strong>Description:</strong>
                            {' '}
                            {tenant.companyInfo.description}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    <Divider />

                    {/* Application Date */}
                    <Typography variant="caption" color="text.secondary">
                      Applied on:
                      {' '}
                      {new Date(tenant.createdAt).toLocaleDateString()}
                    </Typography>

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleApproval(tenant, 'approve')}
                        fullWidth
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => handleApproval(tenant, 'reject')}
                        fullWidth
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Approval Dialog */}
      <Dialog
        open={approvalDialog.open}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {approvalDialog.action === 'approve' ? 'Approve Tenant' : 'Reject Tenant'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to
              {' '}
              <strong>{approvalDialog.action === 'approve' ? 'approve' : 'reject'}</strong>
              {' '}
              the tenant application for
              {' '}
              <strong>{approvalDialog.tenant?.name}</strong>
              ?
            </Typography>

            {approvalDialog.action === 'reject' && (
              <TextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                required
              />
            )}

            {approvalDialog.action === 'approve' && (
              <Alert severity="info">
                Once approved, the tenant will be able to access the platform and invite additional users.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApproval}
            variant="contained"
            color={approvalDialog.action === 'approve' ? 'success' : 'error'}
            disabled={processing || (approvalDialog.action === 'reject' && !rejectionReason.trim())}
          >
            {processing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              approvalDialog.action === 'approve' ? 'Approve' : 'Reject'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantApprovalPage;
